import { type ReactNode, type RefObject } from 'react'

import { Modal, Space, SubText } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import {
  GlobalSearchViewContentAreaStyled,
  GlobalSearchViewHeaderAreaStyled,
  GlobalSearchViewLayoutStyled,
} from './styles/GlobalSearchViewLayoutStyled'
import { type GlobalSearchStateMessage } from '../../../../data-access/types/GlobalSearchResponse'
import { type GlobalSearchRecentItem } from '../../../../value-objects/GlobalSearchRecentItem'
import { type GlobalSearchResultItem } from '../../../../value-objects/GlobalSearchResultItem'
import { type RecentConversation } from '../../../../value-objects/RecentConversation'
import {
  ContentStates,
  type ContentStateEnum,
} from '../../enums/ContentStateEnum'
import {
  type GlobalSearchTab,
  type GlobalSearchTabTypeEnum,
} from '../../enums/GlobalSearchTabTypeEnum'
import { GlobalSearchEmptyState } from '../GlobalSearchEmptyState'
import { GlobalSearchErrorState } from '../GlobalSearchErrorState'
import { GlobalSearchFirstRunState } from '../GlobalSearchFirstRunState'
import { GlobalSearchInput } from '../GlobalSearchInput'
import { GlobalSearchLoadingState } from '../GlobalSearchLoadingState'
import { GlobalSearchRecentConversations } from '../GlobalSearchRecentConversations'
import { GlobalSearchRecentsList } from '../GlobalSearchRecentsList'
import { GlobalSearchResultsList } from '../GlobalSearchResultsList'
import { GlobalSearchResultTabs } from '../GlobalSearchResultTabs'
import { GlobalSearchSuggestedPrompts } from '../GlobalSearchSuggestedPrompts'
import { GlobalSearchUnsupportedQueryState } from '../GlobalSearchUnsupportedQueryState'

const LISTBOX_ID = 'global-search-listbox'

interface IGlobalSearchViewProps {
  query: string
  showEnterHint: boolean
  contentState: Nullable<ContentStateEnum>
  recentItems: GlobalSearchRecentItem[]
  recentConversations: RecentConversation[]
  resultItems: GlobalSearchResultItem[]
  tabs: Nullable<GlobalSearchTab[]>
  selectedTab: GlobalSearchTabTypeEnum
  showTruncatedNotice: boolean
  noResultsMessage?: Nullable<GlobalSearchStateMessage>
  unsupportedQueryMessage?: Nullable<GlobalSearchStateMessage>
  errorMessage?: Nullable<string>
  inputRef: RefObject<HTMLInputElement>
  highlightedIndex: Nullable<number>
  activeDescendantId: string | undefined
  isExpanded: boolean
  promoBanner?: ReactNode
  paywallBanner?: ReactNode
  resultsBanner?: ReactNode
  onQueryChange: (value: string) => void
  onEnter: () => void
  onNavigateDown: () => void
  onNavigateUp: () => void
  onRecentItemClick: (item: GlobalSearchRecentItem) => void
  onResultItemClick: (item: GlobalSearchResultItem) => void
  onTabChange: (type: GlobalSearchTabTypeEnum) => void
  onSuggestedPromptClick?: (query: string) => void
  onRecentConversationClick?: (conversation: RecentConversation) => void
}

export const GlobalSearchView = ({
  query,
  showEnterHint,
  contentState,
  recentItems,
  recentConversations,
  resultItems,
  tabs,
  selectedTab,
  showTruncatedNotice,
  noResultsMessage,
  unsupportedQueryMessage,
  errorMessage,
  inputRef,
  highlightedIndex,
  activeDescendantId,
  isExpanded,
  promoBanner,
  paywallBanner,
  resultsBanner,
  onQueryChange,
  onEnter,
  onNavigateDown,
  onNavigateUp,
  onRecentItemClick,
  onResultItemClick,
  onTabChange,
  onSuggestedPromptClick,
  onRecentConversationClick,
}: IGlobalSearchViewProps) => {
  const intl = useProcurifyIntl()

  const showRecentConversations =
    query === '' &&
    recentConversations.length > 0 &&
    onRecentConversationClick !== undefined &&
    (contentState === null || contentState === ContentStates.RECENTS)

  return (
    <GlobalSearchViewLayoutStyled>
      <GlobalSearchViewHeaderAreaStyled>
        <Modal.Content pt={0} pr={6} pb={0}>
          <Space direction='horizontal' size='small' align='center'>
            <Space.Item grow={1} flex>
              <GlobalSearchInput
                listboxId={LISTBOX_ID}
                query={query}
                inputRef={inputRef}
                activeDescendantId={activeDescendantId}
                isExpanded={isExpanded}
                onQueryChange={onQueryChange}
                onEnter={onEnter}
                onNavigateDown={onNavigateDown}
                onNavigateUp={onNavigateUp}
              />
            </Space.Item>
            {showEnterHint && (
              <Space.Item flex>
                <SubText inline noWrap color='textTertiary'>
                  {intl.formatMessage(messages.enterToSearchHint)}
                </SubText>
              </Space.Item>
            )}
          </Space>
        </Modal.Content>
      </GlobalSearchViewHeaderAreaStyled>

      <GlobalSearchViewContentAreaStyled>
        {paywallBanner && (
          <Modal.Content pl={0.5} pt={0} pr={0.5} pb={1}>
            {paywallBanner}
          </Modal.Content>
        )}
        {!paywallBanner && (
          <>
            {showRecentConversations && (
              <Modal.Content pl={0.5} pt={0} pr={0.5} pb={0.5}>
                <GlobalSearchRecentConversations
                  conversations={recentConversations}
                  onConversationClick={onRecentConversationClick}
                />
              </Modal.Content>
            )}
            {contentState === null &&
              query === '' &&
              !showRecentConversations && (
                <Modal.Content pl={0.5} pt={0} pr={0.5} pb={1}>
                  <GlobalSearchFirstRunState />
                </Modal.Content>
              )}
            {contentState === null &&
              query === '' &&
              onSuggestedPromptClick && (
                <Modal.Content pl={0.5} pt={0} pr={0.5} pb={1}>
                  <GlobalSearchSuggestedPrompts
                    onPromptClick={onSuggestedPromptClick}
                  />
                </Modal.Content>
              )}
            {contentState === ContentStates.RECENTS && (
              <Modal.Content pl={0.5} pt={0} pr={0.5} pb={1}>
                <GlobalSearchRecentsList
                  listboxId={LISTBOX_ID}
                  items={recentItems}
                  highlightedIndex={highlightedIndex}
                  onRecentItemClick={onRecentItemClick}
                />
              </Modal.Content>
            )}
            {promoBanner &&
              query === '' &&
              (contentState === null ||
                contentState === ContentStates.RECENTS) && (
                <Modal.Content pl={0.5} pt={0} pr={0.5} pb={1}>
                  {promoBanner}
                </Modal.Content>
              )}
            {contentState === ContentStates.LOADING && (
              <Modal.Content pl={0.5} pt={0} pr={0.5} pb={1}>
                <GlobalSearchLoadingState />
              </Modal.Content>
            )}
            {resultsBanner &&
              (contentState === ContentStates.RESULTS ||
                contentState === ContentStates.EMPTY) && (
                <Modal.Content pl={0.5} pt={0} pr={0.5} pb={1}>
                  {resultsBanner}
                </Modal.Content>
              )}
            {contentState === ContentStates.RESULTS && (
              <>
                {tabs && (
                  <Modal.Content pl={0.5} pt={0} pr={0.5} pb={0.5}>
                    <GlobalSearchResultTabs
                      tabs={tabs}
                      selectedTab={selectedTab}
                      onTabChange={onTabChange}
                    />
                  </Modal.Content>
                )}
                <Modal.Content pl={0.5} pt={0} pr={0.5} pb={1}>
                  <GlobalSearchResultsList
                    key={selectedTab}
                    listboxId={LISTBOX_ID}
                    items={resultItems}
                    showTruncatedNotice={showTruncatedNotice}
                    highlightedIndex={highlightedIndex}
                    onResultItemClick={onResultItemClick}
                  />
                </Modal.Content>
              </>
            )}
            {contentState === ContentStates.EMPTY && (
              <Modal.Content pl={0.5} pt={0} pr={0.5} pb={1}>
                <GlobalSearchEmptyState message={noResultsMessage} />
              </Modal.Content>
            )}
            {contentState === ContentStates.UNSUPPORTED_QUERY && (
              <Modal.Content pl={0.5} pt={0} pr={0.5} pb={1}>
                <GlobalSearchUnsupportedQueryState
                  message={unsupportedQueryMessage}
                />
              </Modal.Content>
            )}
            {contentState === ContentStates.ERROR && (
              <Modal.Content pl={0.5} pt={0} pr={0.5} pb={1}>
                <GlobalSearchErrorState message={errorMessage} />
              </Modal.Content>
            )}
          </>
        )}
      </GlobalSearchViewContentAreaStyled>
    </GlobalSearchViewLayoutStyled>
  )
}
